import React from 'react';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import fileAccepted from 'attr-accept'; // utils from react-dropzone
import { useCurrentUser } from '@docavenue/components';
// import { errorActions } from '@docavenue/core';

import { Button } from '@mui/material';

import { useTranslation } from '@/src/i18n';
import Loader from '../../atoms/Loader/Loader';
import File from './File';
import CategorySelect from './CategorySelect';
import ProfileSelect, {
  ProfileSelectProps as ProfileSelectPropsType,
} from './ProfileSelect';
import { ACCEPT_FILE_PATTERN } from '@/src/constants';
import ShareCheckbox from './ShareCheckbox';
import DynamicFormWithDialog from '@/components/organisms/Forms/DynamicFormWithDialog';
import { PatientUser } from '@/components/molecules/WithAuth/types';
import { screebEventTrack } from '@/src/screeb';

export type DocumentUploadFormProps = {
  onSuccess: () => any;
  onUpload: (values: object, onSuccessCb: () => any) => any;
  tlc: boolean;
  ProfileSelectProps?: Partial<ProfileSelectPropsType>;
  initialValues?: {
    file?: File;
    profileId?: string;
  };
  shareCheckbox?: boolean;
  onCancel: () => void;
};

const SIZE_20MB = 20 * 1024 * 1024;

const documentUploadFormValidationSchema = isTlc =>
  yup.object().shape({
    category: yup.string().required(),
    profileId: isTlc ? yup.string() : yup.string().required(),
    shareCheckbox: yup.boolean().when('cards', {
      is: values => values?.length > 0,
      then: (childSchema: yup.BooleanSchema) => childSchema.equals([true]),
      otherwise: (childSchema: yup.BooleanSchema) =>
        childSchema.equals([false]),
    }),
  });

const totalDocumentSizeSelector = state => state?.documents?.totalDocumentSize;

const totalSizeAllowedSelector = state => state?.documents?.totalSizeAllowed;

const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({
  onSuccess,
  onUpload,
  tlc = false,
  ProfileSelectProps,
  initialValues = {},
  shareCheckbox,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [statusUpload, setStateUpload] = React.useState('ready');
  const patients =
    useCurrentUser<PatientUser>()?.userPatientInformation.userPatientProfiles ??
    [];
  const totalDocumentSize = useSelector(totalDocumentSizeSelector);
  const totalSizeAllowed = useSelector(totalSizeAllowedSelector);

  const validateFile = React.useCallback((file: File) => {
    let error: string | undefined;

    if (!file || !fileAccepted(file, ACCEPT_FILE_PATTERN)) {
      error = 'error:EXTENSION_DOCUMENT_NOT_ALLOWED';
    } else {
      const isFullStorage = file.size + totalDocumentSize > totalSizeAllowed;
      if (file.size > SIZE_20MB) {
        error = 'error:TOO_LARGE_DOCUMENT_SIZE';
      }
      if (isFullStorage) {
        error = 'error:EXCEEDED_SIZE_SPACE';
      }

      // If you want show dialog error, just remove comment
      /*
      if (isFullStorage)
        dispatch(
          errorActions.addError(
            {
              resource: 'documents',
              type: 'CREATE',
            },
            {
              subCodes: ['EXCEEDED_SIZE_SPACE'],
              message: 'EXCEEDED_SIZE_SPACE',
            },
          ),
          );
       */
    }
    return error;
  }, []);

  const fields: any = [
    {
      name: 'file',
      component: File,
      validate: validateFile,
    },
    {
      name: 'category',
      component: CategorySelect,
    },
  ];

  if (!tlc) {
    fields.push({
      name: 'profileId',
      component: ProfileSelect,
      ...ProfileSelectProps,
    });
  }

  if (shareCheckbox && !tlc) {
    fields.push({
      name: 'shareCheckbox',
      component: ShareCheckbox,
    });
  }

  const onActionSuccess = () => {
    onSuccess();
    setStateUpload('ready');
  };

  const onSubmit = React.useCallback(
    (values, onSuccessCb) => {
      screebEventTrack('Document import');
      setStateUpload('uploading');
      onUpload(values, onSuccessCb);
    },
    [onUpload],
  );

  return (
    <DynamicFormWithDialog
      initialValues={{
        file: null,
        profileId: patients.length === 1 ? patients[0].id : '',
        category: '',
        shareCheckbox: false,
        ...initialValues,
      }}
      initialTouched={{
        file: Boolean(initialValues?.file),
      }}
      fields={fields}
      onSubmit={values => onSubmit(values, onActionSuccess)}
      validationSchema={documentUploadFormValidationSchema(tlc)}
      validateOnMount
      submit={
        <Button
          data-testid="download-btn"
          variant="contained"
          color="secondary"
          aria-label={t('accessibility_documents_validate_button')}
          startIcon={
            statusUpload === 'uploading' && (
              <Loader
                size={17}
                color="secondary"
                useContrastText
                sx={{ mr: 1 }}
              />
            )
          }
        >
          {t('action_validate')}
        </Button>
      }
      cancelButton={
        <Button
          variant="outlined"
          color="primary"
          aria-label={t('accessibility_documents_action_cancel')}
          onClick={onCancel}
        >
          {t('action_cancel')}
        </Button>
      }
    />
  );
};

export default DocumentUploadForm;
